import _ from 'lodash';
import debug from 'debug';
import { StorySettingsType } from 'types/story';
import { int } from 'common/utils/helpers';
import * as cssMQ from './media-queries';

export { updateStoryLastCardId, getStoryLastCardId } from './history-storage';

export { cssMQ };
export const attrSelector = selector => `[data-${_.kebabCase(selector)}]`;

const log = debug('client:utils:AnswersStorage');

type StoredDataT = Record<
	'cards',
	Record<
		string, // card id
		string[] | undefined // answer id arr
	>
>;

export class AnswersStorage {
	storyId: string;

	cardId: string;

	isMultiple: boolean;

	itemName: string;

	/**
	 * @param storyId {String}
	 * @param cardId {String}
	 * @param isMultiple {Boolean} is allowed to select multiple answers
	 */
	constructor({ storyId = '', cardId = '', isMultiple = false }) {
		this.storyId = storyId;
		this.cardId = cardId;
		this.isMultiple = isMultiple;
		this.itemName = `sc-as-${storyId}`;
	}

	get storedData(): StoredDataT {
		const data = localStorage.getItem(this.itemName);
		const noData = JSON.stringify({ cards: {} });
		return JSON.parse(data || noData);
	}

	add(answerId: string) {
		const prevAnswers = this.storedData.cards[this.cardId] ?? [];
		const answers = this.isMultiple ? _.uniq([...prevAnswers, answerId]) : [answerId];

		const nextData = {
			...this.storedData,
			cards: {
				...this.storedData.cards,
				[this.cardId]: answers,
			},
		};

		localStorage.setItem(this.itemName, JSON.stringify(nextData));
	}

	/**
	 * Get score for played cards
	 * @param storySettings
	 * @return {number} relative score
	 */
	totalResults(storySettings: StorySettingsType) {
		const RESULT = {
			scoreMax: 0, // Maximum score - the sum of the maximum score for the cards played
			scoreTotal: 0, // Total score - the amount of score received for the cards played
			scoreRelative: 0, // Relative score - the ratio of the total score to the maximum (0 - 100)
			answersTotal: 0,
			answersCorrect: 0,
		};

		Object.entries(this.storedData.cards).forEach(([cardId, chosenAnswerIds = []]) => {
			const { score = 0, answers = {} } = storySettings.cards?.[cardId] ?? {};

			// Get card score
			const cardScore = int(score);
			if (cardScore) RESULT.scoreMax += cardScore;

			// Get max answer score per card (eg. PersonalityTest card)
			const answerMaxScore = _.max(Object.values(answers).map(answer => int(answer.score)));
			if (answerMaxScore) RESULT.scoreMax += answerMaxScore;

			// sum up the score for chosen answers
			chosenAnswerIds.forEach(answerId => {
				// Add card score for a correct answer (eg. Trivia card)
				// TODO: if isMultiple score should be incremented partially in depends on count of correct answers
				const isCorrect = _.get(answers, `${answerId}.isCorrect`);
				RESULT.scoreTotal += isCorrect ? cardScore : 0;

				// inc counters
				RESULT.answersTotal += 1;
				if (isCorrect) RESULT.answersCorrect += 1;

				// Add answer score (PersonalityTest card)
				const answerScore = int(answers?.[answerId]?.score);
				if (answerScore) RESULT.scoreTotal += answerScore;
			});
		});

		RESULT.scoreRelative = _.multiply(RESULT.scoreTotal / RESULT.scoreMax || 0, 100);

		log('result', RESULT);

		return RESULT;
	}
}

class _AnswerDelta {
	private delta: Record<string, number> = {};

	start(cardId: string) {
		this.delta[cardId] = Date.now();

		log('_AnswerDelta start', this.delta[cardId]);
	}

	end(cardId: string) {
		if (!this.delta[cardId]) return;

		this.delta[cardId] = _.round((Date.now() - this.delta[cardId]) / 1000, 3);

		log('_AnswerDelta end', this.delta[cardId]);
	}

	get(cardId: string) {
		return this.delta[cardId];
	}
}

export const AnswerDelta = new _AnswerDelta();
